/**
 * Small rotating call-out chip for the Home hero rail. Cycles through
 * community CTAs to nudge new users to first contribution. Dismissible per
 * session via localStorage.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

const CTAS = [
  {
    key: "report_weather",
    emoji: "🌦️",
    label: "Report Local Weather",
    href: "/weather#community-reports",
    color: "linear-gradient(90deg, #3b82f6 0%, #06b6d4 100%)",
  },
  {
    key: "civic_report",
    emoji: "🚨",
    label: "File a Civic Report",
    href: "/civic",
    color: "linear-gradient(90deg, #ef4444 0%, #f97316 100%)",
  },
  {
    key: "share_story",
    emoji: "✍️",
    label: "Share a Community Story",
    href: "/celebrate",
    color: "linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)",
  },
  {
    key: "post_marketplace",
    emoji: "🛍️",
    label: "Sell Locally on Marketplace",
    href: "/marketplace/new",
    color: "linear-gradient(90deg, #10b981 0%, #14b8a6 100%)",
  },
];

const DISMISS_KEY = "sp_home_callout_dismissed";

export default function HomeCallout() {
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const val = window.localStorage.getItem(DISMISS_KEY);
      if (val) {
        const ts = parseInt(val, 10);
        // Hide for 24h after dismiss
        if (Date.now() - ts < 24 * 3600 * 1000) setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % CTAS.length), 5000);
    return () => clearInterval(id);
  }, [dismissed]);

  if (dismissed) return null;
  const cta = CTAS[idx];

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="home-callout"
      className="mt-6 inline-flex items-center gap-3 rounded-full pl-2 pr-1 py-1 text-sm font-medium shadow-sm animate-in fade-in duration-500"
      style={{ background: cta.color, color: "#ffffff" }}
      key={cta.key}
    >
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-base"
        style={{ background: "rgba(255,255,255,0.2)" }}
        aria-hidden="true"
      >
        {cta.emoji}
      </span>
      <Link
        to={cta.href}
        data-testid={`home-callout-link-${cta.key}`}
        className="inline-flex items-center gap-1 hover:underline"
      >
        {cta.label}
        <ArrowRight className="w-4 h-4" />
      </Link>
      <button
        type="button"
        data-testid="home-callout-dismiss"
        onClick={dismiss}
        className="ml-1 rounded-full w-6 h-6 inline-flex items-center justify-center hover:bg-white/20 transition"
        aria-label="Dismiss"
        title="Dismiss for 24 hours"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
